import React, { Suspense, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { closeModal } from '../../store/modalSlice';
import lazyWithRetry from '../../utils/lazyWithRetry';
import LazyLoadFallback from './LazyLoadFallback';
import { Box, Typography, Button } from '@mui/material';

// Catch all render/load errors of lazy modals so a failure shows a recoverable
// fallback instead of bubbling up and crashing the whole app.
class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    console.error('ModalManager modal error:', error);
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      return this.props.renderError(this.reset);
    }
    return this.props.children;
  }
}

const modalImports = {
  developmentAction: () => import('../addons/development/DevelopmentActionModal'),
  developmentPreferences: () => import('../addons/development/DevelopmentPreferencesModal'),
  deviceSecurity: () => import('./DeviceSecurityModal'),
  userDevices: () => import('./UserDevicesModal'),
  evolutionSheetPdfBuilder: () => import('../addons/evolutionSheet/EvolutionPdfBuilderModal'),
  evolutionSheetPdfPreview: () => import('../addons/evolutionSheet/EvolutionPdfPreviewModal'),
  evolutionSheetPrint: () => import('../addons/evolutionSheet/EvolutionPrintModal'),
  bookingAddon: () => import('../addons/booking/BookingModal'),
  receivablesPreview: () => import('../addons/receivables/ReceivablesPreviewModal'),
  receivablesInvoices: () => import('../addons/receivables/ReceivablesInvoicesModal'),
  uiExtension: () => import('../uiExtensions/ExtensionSurfaces').then(m => ({ default: m.ExtensionModal })),
  selectBusiness: () => import('./SelectBusinessModal'),
  createElement: () => import('./CreateElementModal'),
  confirmDelete: () => import('./ConfirmDeleteModal'),
  confirmArchive: () => import('./ConfirmArchiveModal'),
  confirmAction: () => import('./ConfirmActionModal'),
  deleteFieldValidation: () => import('./DeleteFieldValidationModal'),
  sendEmail: () => import('./SendEmailModal'),
  addEmployee: () => import('./AddEmployeeModal'),
  addGroup: () => import('./AddGroupModal'),
  groupAccess: () => import('./GroupAccessModal'),
  emailBuilder: () => import('./EmailBuilderModal'),
  emailTemplates: () => import('./EmailTemplatesModal'),
  fieldTriggerBuilder: () => import('./FieldTriggerBuilderModal'),
  workflowExecutor: () => import('./WorkflowExecutorModal'),
  workflowVisualizer: () => import('./WorkflowVisualizerModal'),
  fieldTriggerVisualizer: () => import('./FieldTriggerVisualizerModal'),
  automationBuilder: () => import('./AutomationBuilderModal'),
  category: () => import('./CategoryModal'),
  script: () => import('./ScriptModal'),
  assignEmployee: () => import('./AssignEmployeeModal'),
  contactMemoryDate: () => import('../contactMemory/FollowUpDateModal'),
  engramPreferences: () => import('../contactMemory/EngramPreferencesModal'),
  pdfBuilder: () => import('./PDFBuilderModal'),
  qrCode: () => import('./QRCodeModal'),
  addItem: () => import('./AddItemModal'),
  aiSuggestion: () => import('./AISuggestionModal'),
  convert: () => import('./ConvertElementModal'),
  duplicateElement: () => import('./DuplicateElementModal'),
  merge: () => import('./MergeContactsModal'),
  print: () => import('./PrintModal'),
  accountCreation: () => import('./AccountCreationModal'),
  createDashboard: () => import('./CreateDashboardModal'),
  blockEditor: () => import('../pulse/BlockEditor'),
  businessDetails: () => import('./BusinessDetailsModal'),
  createMaestroAccount: () => import('./CreateMaestroAccountModal'),
  createMaestroModel: () => import('./CreateMaestroModelModal'),
  manageHours: () => import('./ManageHoursModal'),
  structureLayout: () => import('../settings/StructureLayoutModal'),
  dataMappingBuilder: () => import('../settings/DataMappingBuilderModal'),
  paymentDetails: () => import('./PaymentDetailsModal'),
  blockSettings: () => import('./BlockSettingsModalContainer'),
  dataTables: () => import('./DataTablesModal'),
  registryEditor: () => import('./RegistryEditorModal'),
  movementsLedger: () => import('./MovementsLedgerModal'),
  CreateStructureWizardModal: () => import('../settings/CreateStructureWizardModal'),
  structureSettings: () => import('./StructureSettingsModal'),
  approvalFlow: () => import('./ApprovalFlowModal'),
  elementFlows: () => import('./ElementFlowsModal'),
  multiPayment: () => import('./MultiPaymentModal'),
  multiBilling: () => import('./MultiBillingModal'),
  createProjectFromQuote: () => import('./CreateProjectFromQuoteModal'),
  reportSettings: () => import('./ReportSettingsModal'),
  moduleSettings: () => import('./ModuleSettingsModal'),
  newsModal: () => import('./NewsModal'),
  sendBulkEmail: () => import('./SendBulkEmailModal'),
  structureLinkedItems: () => import('./StructureLinkedItemsModal'),
  itemActionFlow: () => import('../modals/ItemActionFlowModal'),
  cellManager: () => import('../modals/CellManagerModal'),
  elementHistory: () => import('./ElementHistoryModal'),
  elementRevisions: () => import('./ElementRevisionsModal'),
  commandPalette: () => import('./CommandPalette'),
  structureAdvisor: () => import('./StructureAdvisorModal'),
  confirmBulkDelete: () => import('./ConfirmBulkDeleteModal'),
  connectorInstall: () => import('../marketplace/ConnectorInstallModal'),
  connectorOAuth: () => import('../marketplace/OAuthInstallModal'),
  connectorConfig: () => import('./ConnectorSidebar'),
  calendarView: () => import('./CalendarModal'),
  createModule: () => import('./CreateModuleModal'),
  createFlow: () => import('./CreateFlowModal'),
  flowEditor: () => import('./FlowEditorModal'),
  addonSetup: () => import('../addons/AddonSetupWizard'),
  sandboxMode: () => import('./SandboxModeModal'),
  addTime: () => import('../addons/timesheet/AddTimeModal'),
  inventoryCommand: () => import('../addons/inventory/InventoryCommandModal'),
  economicCommand: () => import('../addons/economics/EconomicCommandModal'),
  projectExecution: () => import('../projects/ProjectExecutionModal'),
  projectQuote: () => import('../addons/projects/ProjectQuoteModal'),
  projectMilestoneActivation: () => import('../addons/projects/ProjectMilestoneActivationModal'),
  profitability: () => import('../addons/profitability/ProfitabilityModal'),
  profitabilityAllocation: () => import('../addons/profitability/ProfitabilityAllocationModal'),
  profitabilityConfiguration: () => import('../addons/profitability/ProfitabilityConfigurationModal'),
  analysisWorkspace: () => import('../addons/analysis/AnalysisModal'),
  analysisProfile: () => import('../addons/analysis/AnalysisProfileModal'),
  pageLayoutPicker: () => import('../modals/PageLayoutPickerModal'),
  layoutAiAssistant: () => import('../layout-ai/LayoutAiAssistantModal'),
  commissionRule: () => import('../addons/commission/CommissionRuleModal'),
  voidCommission: () => import('../addons/commission/VoidCommissionModal'),
  confirmClearElements: () => import('./ConfirmClearElementsModal'),
  leaveRequest: () => import('../addons/hr/LeaveRequestModal'),
  avatarCreation: () => import('../account/AvatarCreationModal'),
};

// One cached lazy component per modalType. A retry replaces the entry so a fresh
// import is attempted, while keeping the cache bounded (no per-attempt leak).
const lazyCache = {};

function getLazyComponent(modalType, attempt) {
  const importFn = modalImports[modalType];
  if (!importFn) return null;

  const cached = lazyCache[modalType];
  if (!cached || cached.attempt !== attempt) {
    lazyCache[modalType] = { attempt, component: lazyWithRetry(importFn) };
  }
  return lazyCache[modalType].component;
}

const ModalErrorFallback = ({ onRetry, onClose }) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        backgroundColor: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('loadingError', { defaultValue: 'Une erreur est survenue lors du chargement.' })}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Button variant="contained" size="small" onClick={onRetry}>
          {t('tryAgain')}
        </Button>
        <Button variant="outlined" size="small" onClick={onClose}>
          {t('close')}
        </Button>
      </Box>
    </Box>
  );
};

const ModalManager = () => {
  const { modalType, modalProps } = useSelector((state) => state.modals);
  const dispatch = useDispatch();

  const handleClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  const handleRetry = useCallback(() => {
    window.location.reload();
  }, []);

  if (!modalType) {
    return null;
  }

  const SpecificModal = getLazyComponent(modalType, 0);

  if (!SpecificModal) {
    return null;
  }

  return (
    <ModalErrorBoundary
      key={modalType}
      renderError={() => (
        <ModalErrorFallback
          onRetry={handleRetry}
          onClose={handleClose}
        />
      )}
    >
      <Suspense fallback={<LazyLoadFallback onRetry={handleRetry} />}>
        <SpecificModal {...modalProps} />
      </Suspense>
    </ModalErrorBoundary>
  );
};

export default ModalManager;
